import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { ConfettiBurst } from "../../feedback/Celebration";
import { useCompleteOnce } from "./engineUtils";
import { Art, Counter, IconArrow, IconCheck, IconSpeaker, ROOT_STYLE, RoundButton, SparkleBurst, StickerTile, choiceGrid, frost, tileMultForCount, tileSize, useLetterAccent, useTileFeedback, wait, withTimeout } from "./tapUtils";

/**
 * Call-and-repeat memory: the words in `round.sequence` are spoken and lit
 * one by one, then the child taps them back in order. A slip replays the
 * call. Completes after every round succeeds.
 */
export default function CallSequenceGame({ letter, config = {}, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { accent, deep } = useLetterAccent(letter);
  const bp = useBreakpoint();
  const compact = bp === "xs" || bp === "sm";
  const rounds = useMemo(() => (config.rounds || []).filter((r) => r?.sequence?.length), [config.rounds]);
  const byWord = useMemo(() => {
    const m = new Map();
    (config.items || []).forEach((it) => { if (it?.word) m.set(String(it.word).toLowerCase(), it); });
    return m;
  }, [config.items]);

  const [ri, setRi] = useState(0);
  const [phase, setPhase] = useState("listen"); // listen | reply | solved
  const [progress, setProgress] = useState(0);
  const [highlight, setHighlight] = useState(-1); // index into round.sequence while listening
  const [spoken, setSpoken] = useState({}); // word -> times lit (drives the tile pulse)
  const [listenTick, setListenTick] = useState(0);
  const { shakes, pulses, shakeId, pulseId } = useTileFeedback();
  const round = rounds[ri];
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  // Play the call.
  useEffect(() => {
    if (!round) return undefined;
    let cancelled = false;
    setPhase("listen");
    setProgress(0);
    setHighlight(-1);
    (async () => {
      await wait(listenTick === 0 ? (reduceMotion ? 300 : 900) : 400);
      for (let i = 0; i < round.sequence.length; i += 1) {
        if (cancelled) return;
        const w = round.sequence[i];
        setHighlight(i);
        setSpoken((m) => ({ ...m, [w]: (m[w] || 0) + 1 }));
        sfx?.("pop", { volume: 0.5 });
        await withTimeout(speak?.(w, { dedupe: false }), 1800);
        if (cancelled) return;
        await wait(reduceMotion ? 220 : 480);
        setHighlight(-1);
        await wait(reduceMotion ? 80 : 160);
      }
      if (cancelled) return;
      setPhase("reply");
      speak?.("Your turn.");
    })();
    return () => { cancelled = true; };
  }, [ri, listenTick, round, reduceMotion, sfx, speak]);

  useEffect(() => {
    if (!rounds.length) finish();
  }, [rounds.length, finish]);

  useEffect(() => {
    if (phase !== "solved" || !round) return undefined;
    const t = setTimeout(() => {
      if (!alive.current) return;
      if (ri + 1 >= rounds.length) { finish(); return; }
      setRi((x) => x + 1);
      setListenTick(0);
    }, reduceMotion ? 350 : 1200);
    return () => clearTimeout(t);
  }, [phase, ri, rounds.length, finish, reduceMotion, round]);

  const replayCall = useCallback(() => setListenTick((t) => t + 1), []);

  if (!round) return <div style={ROOT_STYLE} />;

  const tap = (w) => {
    if (phase !== "reply") return;
    const expected = round.sequence[progress];
    if (String(w).toLowerCase() !== String(expected).toLowerCase()) {
      shakeId(w);
      onWrong?.();
      setProgress(0);
      setTimeout(() => { if (alive.current) replayCall(); }, reduceMotion ? 300 : 700);
      return;
    }
    sfx?.("pop");
    speak?.(w, { dedupe: false });
    pulseId(w);
    const next = progress + 1;
    setProgress(next);
    if (next >= round.sequence.length) {
      sfx?.("correct");
      setPhase("solved");
    }
  };

  const choices = round.choices || round.sequence;
  const mult = tileMultForCount(choices.length);
  const finalRound = phase === "solved" && ri + 1 >= rounds.length;
  const listening = phase === "listen";

  const status = listening ? "Listen" : phase === "solved" ? "You got it" : "Your turn";

  return (
    <div style={ROOT_STYLE}>
      <ConfettiBurst active={finalRound} originY={40} count={36} reduceMotion={reduceMotion} />
      <Counter done={ri + (phase === "solved" ? 1 : 0)} total={rounds.length} accent={accent} />

      {/* Call strip: status badge, one slot per word in the sequence, replay. */}
      <div style={frost({ padding: compact ? "8px 10px" : "10px 14px 10px 12px", display: "flex", alignItems: "center", gap: compact ? 10 : 14, flexShrink: 0, margin: `${compact ? 34 : 0}px auto ${compact ? 8 : 12}px`, width: "min(100%, 560px)", boxSizing: "border-box", borderColor: listening ? `${accent}55` : LEARN.color.line, transition: "border-color 200ms ease" })}>
        <span
          aria-hidden="true"
          style={{
            width: compact ? 40 : 48, height: compact ? 40 : 48, borderRadius: compact ? 12 : 14, flexShrink: 0,
            background: `linear-gradient(160deg, ${accent} 0%, ${deep} 100%)`, color: "#fff", boxShadow: `0 3px 0 ${deep}`,
            display: "grid", placeItems: "center",
          }}
        >
          {listening ? <IconSpeaker size={compact ? 20 : 24} /> : phase === "solved" ? <IconCheck size={compact ? 20 : 24} /> : <IconArrow size={compact ? 20 : 24} dir="down" />}
        </span>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          <div aria-live="polite" style={{ fontSize: compact ? 15 : 17, fontWeight: 700, color: listening ? deep : phase === "solved" ? LEARN.color.mintDeep : LEARN.color.ink, lineHeight: 1.1, transition: "color 200ms ease" }}>
            {status}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {round.sequence.map((w, i) => {
              const it = byWord.get(String(w).toLowerCase());
              const lit = listening && highlight === i;
              const show = listening ? lit : i < progress;
              return (
                <motion.span
                  key={`${w}-${i}`}
                  animate={lit && !reduceMotion ? { scale: [1, 1.15, 1] } : { scale: 1 }}
                  transition={{ duration: 0.4 }}
                  aria-label={show ? w : `Step ${i + 1}`}
                  style={{
                    width: tileSize(0.8), height: tileSize(0.8), borderRadius: "24%", display: "flex", alignItems: "center", justifyContent: "center",
                    background: show ? "#fff" : `${accent}12`, border: show ? `2px solid ${accent}` : `2px dashed ${accent}77`,
                    boxShadow: show ? LEARN.shadow.card : "none", transition: reduceMotion ? "none" : "background 200ms, border-color 200ms",
                  }}
                >
                  {show && <Art letter={letter} assetName={it?.assetName} word={w} alt="" />}
                </motion.span>
              );
            })}
          </div>
        </div>
        <RoundButton ariaLabel="Play the call again" onClick={replayCall} disabled={listening} accent={accent} deep={deep} reduceMotion={reduceMotion} size={compact ? 44 : 50}>
          <IconSpeaker size={compact ? 20 : 22} />
        </RoundButton>
      </div>

      {/* Choices */}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={ri}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            style={choiceGrid(choices.length, compact)}
          >
            {choices.map((w, i) => {
              const it = byWord.get(String(w).toLowerCase());
              const lit = listening && highlight >= 0 && String(round.sequence[highlight]).toLowerCase() === String(w).toLowerCase();
              return (
                <div key={w} style={{ position: "relative" }}>
                  <StickerTile
                    letter={letter}
                    assetName={it?.assetName}
                    word={w}
                    label={w}
                    size={mult}
                    index={i}
                    accent={accent}
                    reduceMotion={reduceMotion}
                    shake={shakes[w] || 0}
                    pulse={(pulses[w] || 0) + (spoken[w] || 0)}
                    state={lit ? "correct" : listening ? "dim" : phase === "solved" ? "done" : "idle"}
                    disabled={phase !== "reply"}
                    onTap={() => tap(w)}
                    style={listening && !lit ? { opacity: 0.75 } : undefined}
                  />
                  <SparkleBurst trigger={pulses[w] || 0} color={accent} reduceMotion={reduceMotion} />
                </div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
